import { getMondialiWeekWindows } from './mondialiRouting';

const roundTo1Decimal = (value) => Math.round(Number(value) * 10) / 10;
const MONDIALI_WEEKLY_LEADERBOARD_MIN_PLAYERS = 5;

const maskClerkUserIdForPublic = (clerkUserId) => {
  if (!clerkUserId || clerkUserId.length <= 6) {
    return clerkUserId || '';
  }

  const maskedUntil = Math.min(clerkUserId.length, 27);
  return `${clerkUserId.slice(0, 6)}${'*'.repeat(maskedUntil - 6)}${clerkUserId.slice(maskedUntil)}`;
};

export const hasRangeEnded = (endAt, now = new Date()) => {
  const endDate = new Date(endAt);
  if (Number.isNaN(endDate.getTime())) {
    return false;
  }

  return now.getTime() >= endDate.getTime();
};

const DEMO_TEAMS = [
  { country_alpha2: 'IT', country_name_it: 'Italia', country_name_en: 'Italy', usernamePrefix: 'azzurri' },
  { country_alpha2: 'BR', country_name_it: 'Brasile', country_name_en: 'Brazil', usernamePrefix: 'verdeoro' },
  { country_alpha2: 'AR', country_name_it: 'Argentina', country_name_en: 'Argentina', usernamePrefix: 'albiceleste' },
  { country_alpha2: 'ES', country_name_it: 'Spagna', country_name_en: 'Spain', usernamePrefix: 'furia' },
  { country_alpha2: 'FR', country_name_it: 'Francia', country_name_en: 'France', usernamePrefix: 'bleus' },
  { country_alpha2: 'DE', country_name_it: 'Germania', country_name_en: 'Germany', usernamePrefix: 'panzer' },
  { country_alpha2: 'JP', country_name_it: 'Giappone', country_name_en: 'Japan', usernamePrefix: 'sakura' },
  { country_alpha2: 'PT', country_name_it: 'Portogallo', country_name_en: 'Portugal', usernamePrefix: 'lusitani' },
  { country_alpha2: 'NL', country_name_it: 'Paesi Bassi', country_name_en: 'Netherlands', usernamePrefix: 'oranje' },
  { country_alpha2: 'US', country_name_it: 'Stati Uniti', country_name_en: 'United States', usernamePrefix: 'stars' },
];

const buildDemoTeamPlayers = (team, teamIndex) => {
  if (team.country_alpha2 === 'US') {
    return Array.from({ length: 13 }, (_, playerIndex) => ({
      clerk_user_id: `user_demo_${team.country_alpha2.toLowerCase()}_${String(playerIndex + 1).padStart(2, '0')}`,
      username: `${team.usernamePrefix}${String(playerIndex + 1).padStart(2, '0')}`,
      stelline_total: 0,
    }));
  }

  if (team.country_alpha2 === 'NL') {
    const scriptedScores = [910, 690, 470, 250];
    return scriptedScores.map((score, playerIndex) => ({
      clerk_user_id: `user_demo_${team.country_alpha2.toLowerCase()}_${String(playerIndex + 1).padStart(2, '0')}`,
      username: `${team.usernamePrefix}${String(playerIndex + 1).padStart(2, '0')}`,
      stelline_total: score,
    }));
  }

  if (team.country_alpha2 === 'PT') {
    const scriptedScores = [980, 760, 540, 320, 120];
    return scriptedScores.map((score, playerIndex) => ({
      clerk_user_id: `user_demo_${team.country_alpha2.toLowerCase()}_${String(playerIndex + 1).padStart(2, '0')}`,
      username: `${team.usernamePrefix}${String(playerIndex + 1).padStart(2, '0')}`,
      stelline_total: score,
    }));
  }

  const playersTotal = 12 + (teamIndex % 7);
  const baseScore = 2450 - (teamIndex * 135);
  return Array.from({ length: playersTotal }, (_, playerIndex) => {
    const playerScore = Math.max(90, baseScore - (playerIndex * 57) - ((teamIndex % 3) * 9));
    return {
      clerk_user_id: `user_demo_${team.country_alpha2.toLowerCase()}_${String(playerIndex + 1).padStart(2, '0')}`,
      username: `${team.usernamePrefix}${String(playerIndex + 1).padStart(2, '0')}`,
      stelline_total: playerScore,
    };
  });
};

const buildDemoPodiumRows = ({ countries, startAt }) => {
  const awardedClerkUserIds = new Set();
  const rangeStartAt = new Date(startAt);
  const officialWeeks = getMondialiWeekWindows();

  officialWeeks.forEach((weekWindow) => {
    const officialWeekEndAt = new Date(weekWindow.endTimestamp + 1000);
    if (officialWeekEndAt.getTime() > rangeStartAt.getTime()) {
      return;
    }

    countries
      .filter((country) => country.is_weekly_leaderboard_eligible)
      .slice(0, 3)
      .forEach((country) => {
        const winner = country.players.find((player) => !awardedClerkUserIds.has(player.clerk_user_id));
        if (winner) {
          awardedClerkUserIds.add(winner.clerk_user_id);
        }
      });
  });

  return countries
    .filter((country) => country.is_weekly_leaderboard_eligible)
    .slice(0, 3)
    .map((country) => {
      const winner = country.players.find((player) => !awardedClerkUserIds.has(player.clerk_user_id));
      if (!winner) {
        return null;
      }

      awardedClerkUserIds.add(winner.clerk_user_id);
      return {
        place: country.place,
        country_alpha2: country.country_alpha2,
        username: winner.username,
        clerk_user_id: maskClerkUserIdForPublic(winner.clerk_user_id),
      };
    })
    .filter(Boolean);
};

export const buildDemoRangeLeaderboardPayload = ({ startAt, endAt, now = new Date(endAt) }) => {
  const demoCountries = DEMO_TEAMS.map((team, teamIndex) => {
    const players = buildDemoTeamPlayers(team, teamIndex)
      .filter((player) => Number(player.stelline_total) > 0)
      .sort((left, right) => {
        const scoreDiff = Number(right.stelline_total) - Number(left.stelline_total);
        if (scoreDiff !== 0) {
          return scoreDiff;
        }
        return String(left.username).localeCompare(String(right.username), 'it', { sensitivity: 'base' });
      });

    const top11Players = players.slice(0, 11);
    const top11Average = top11Players.length > 0
      ? top11Players.reduce((sum, player) => sum + player.stelline_total, 0) / top11Players.length
      : 0;

    return {
      ...team,
      players,
      active_players_count: players.length,
      top11_count: Math.min(11, players.length),
      average_top11_stelline: roundTo1Decimal(top11Average),
      is_weekly_leaderboard_eligible: players.length >= MONDIALI_WEEKLY_LEADERBOARD_MIN_PLAYERS,
      place: null,
    };
  });

  demoCountries.sort((left, right) => {
    if (left.is_weekly_leaderboard_eligible !== right.is_weekly_leaderboard_eligible) {
      return left.is_weekly_leaderboard_eligible ? -1 : 1;
    }
    const scoreDiff = Number(right.average_top11_stelline) - Number(left.average_top11_stelline);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    return left.country_name_it.localeCompare(right.country_name_it, 'it', { sensitivity: 'base' });
  });

  demoCountries.filter((country) => country.is_weekly_leaderboard_eligible).forEach((country, index) => {
    country.place = index + 1;
  });

  return {
    success: true,
    start_at: startAt,
    end_at: endAt,
    countries: demoCountries,
    podium: hasRangeEnded(endAt, now) ? buildDemoPodiumRows({ countries: demoCountries, startAt }) : null,
  };
};

export const buildDemoTopPlayerLeaderboardPayload = ({ startAt, endAt, now = new Date(endAt) }) => {
  const demoPlayers = DEMO_TEAMS.flatMap((team, teamIndex) =>
    buildDemoTeamPlayers(team, teamIndex).map((player) => ({
      clerk_user_id: player.clerk_user_id,
      username: player.username,
      country_alpha2: team.country_alpha2,
      country_name_it: team.country_name_it,
      country_name_en: team.country_name_en,
      stelline_total: player.stelline_total,
    }))
  );

  demoPlayers.sort((left, right) => {
    const scoreDiff = Number(right.stelline_total) - Number(left.stelline_total);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    const usernameCompare = String(left.username).localeCompare(String(right.username), 'it', { sensitivity: 'base' });
    if (usernameCompare !== 0) {
      return usernameCompare;
    }
    return String(left.country_alpha2).localeCompare(String(right.country_alpha2), 'it', { sensitivity: 'base' });
  });

  return {
    success: true,
    start_at: startAt,
    end_at: endAt,
    players: demoPlayers.map((player, index) => ({
      place: index + 1,
      username: player.username,
      country_alpha2: player.country_alpha2,
      country_name_it: player.country_name_it,
      country_name_en: player.country_name_en,
      stelline_total: player.stelline_total,
    })),
    podium: hasRangeEnded(endAt, now)
      ? demoPlayers.slice(0, 3).map((player, index) => ({
          place: index + 1,
          country_alpha2: player.country_alpha2,
          username: player.username,
          clerk_user_id: maskClerkUserIdForPublic(player.clerk_user_id),
        }))
      : null,
  };
};

export const buildDemoFinalePayload = ({ startAt, endAt, now = new Date(endAt) }) => {
  const selectedWeeks = getMondialiWeekWindows()
    .filter((weekWindow) => {
      const weekStartAt = new Date(weekWindow.startTimestamp).toISOString();
      const weekEndAt = new Date(weekWindow.endTimestamp + 1000).toISOString();
      return weekStartAt >= startAt && weekEndAt <= endAt;
    })
    .map((weekWindow) => ({
      week_number: weekWindow.weekNumber,
      start_at: new Date(weekWindow.startTimestamp).toISOString(),
      end_at: new Date(weekWindow.endTimestamp + 1000).toISOString(),
    }));

  const durationDays = Math.max(
    1,
    Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / (24 * 60 * 60 * 1000)),
  );

  const teamDetails = DEMO_TEAMS.map((team, teamIndex) => {
    const players = buildDemoTeamPlayers(team, teamIndex)
      .filter((player) => Number(player.stelline_total) > 0)
      .map((player, playerIndex) => ({
        clerk_user_id: player.clerk_user_id,
        username: player.username,
        competition_stelline_total: Number(player.stelline_total) || 0,
        active_days_count: Math.max(1, Math.min(durationDays, durationDays - playerIndex - (teamIndex % 3))),
      }))
      .sort((left, right) => {
        const scoreDiff = Number(right.competition_stelline_total) - Number(left.competition_stelline_total);
        if (scoreDiff !== 0) {
          return scoreDiff;
        }
        return String(left.username).localeCompare(String(right.username), 'it', { sensitivity: 'base' });
      });

    return {
      country_alpha2: team.country_alpha2,
      country_name_it: team.country_name_it,
      country_name_en: team.country_name_en,
      competition_stelline_total: players.reduce((sum, player) => sum + Number(player.competition_stelline_total || 0), 0),
      players,
    };
  }).sort((left, right) => left.country_name_it.localeCompare(right.country_name_it, 'it', { sensitivity: 'base' }));

  const teamDetailsByAlpha2 = new Map(teamDetails.map((team) => [team.country_alpha2, team]));
  const weeklySnapshots = selectedWeeks.map((week) => ({
    ...week,
    countries: buildDemoRangeLeaderboardPayload({
      startAt: week.start_at,
      endAt: week.end_at,
      now,
    }).countries,
  }));

  const countries = [];
  teamDetails.forEach((team) => {
    const weeklyPlacements = weeklySnapshots.map((snapshot) => {
      const matchingCountry = snapshot.countries.find((country) => country.country_alpha2 === team.country_alpha2);
      if (!matchingCountry || matchingCountry.is_weekly_leaderboard_eligible !== true || !Number.isInteger(matchingCountry.place)) {
        return null;
      }

      return {
        week_number: snapshot.week_number,
        place: matchingCountry.place,
        average_top11_stelline: matchingCountry.average_top11_stelline,
      };
    });

    if (weeklyPlacements.some((placement) => placement === null)) {
      return;
    }

    const normalizedPlacements = weeklyPlacements.filter(Boolean);
    const placementSum = normalizedPlacements.reduce((sum, placement) => sum + Number(placement.place), 0);
    countries.push({
      place: 0,
      country_alpha2: team.country_alpha2,
      country_name_it: team.country_name_it,
      country_name_en: team.country_name_en,
      placement_sum: placementSum,
      average_weekly_placement: roundTo1Decimal(placementSum / normalizedPlacements.length),
      competition_stelline_total: team.competition_stelline_total,
      weekly_placements: normalizedPlacements,
    });
  });

  countries.sort((left, right) => {
    const averageDiff = Number(left.average_weekly_placement) - Number(right.average_weekly_placement);
    if (averageDiff !== 0) {
      return averageDiff;
    }
    const totalDiff = Number(right.competition_stelline_total) - Number(left.competition_stelline_total);
    if (totalDiff !== 0) {
      return totalDiff;
    }
    return left.country_name_it.localeCompare(right.country_name_it, 'it', { sensitivity: 'base' });
  });
  countries.forEach((country, index) => {
    country.place = index + 1;
  });

  const finalPlaceByAlpha2 = new Map(countries.map((country) => [country.country_alpha2, country.place]));
  const publicTeamDetails = teamDetails.map((team) => ({
    country_alpha2: team.country_alpha2,
    country_name_it: team.country_name_it,
    country_name_en: team.country_name_en,
    is_final_leaderboard_qualified: finalPlaceByAlpha2.has(team.country_alpha2),
    final_place: finalPlaceByAlpha2.get(team.country_alpha2) || null,
    competition_stelline_total: team.competition_stelline_total,
    players: team.players.map((player) => ({
      username: player.username,
      competition_stelline_total: player.competition_stelline_total,
      active_days_count: player.active_days_count,
    })),
  }));

  const isCompetitionEnded = hasRangeEnded(endAt, now);
  const podium = isCompetitionEnded
    ? countries.slice(0, 3).map((country) => {
        const winner = teamDetailsByAlpha2.get(country.country_alpha2)?.players?.[0];
        if (!winner) {
          return null;
        }
        return {
          place: country.place,
          country_alpha2: country.country_alpha2,
          username: winner.username,
          clerk_user_id: maskClerkUserIdForPublic(winner.clerk_user_id),
        };
      }).filter(Boolean)
    : null;

  const stacanovistiPodium = isCompetitionEnded && countries.length > 0
    ? [...(teamDetailsByAlpha2.get(countries[0].country_alpha2)?.players || [])]
      .sort((left, right) => {
        const daysDiff = Number(right.active_days_count) - Number(left.active_days_count);
        if (daysDiff !== 0) {
          return daysDiff;
        }
        const scoreDiff = Number(right.competition_stelline_total) - Number(left.competition_stelline_total);
        if (scoreDiff !== 0) {
          return scoreDiff;
        }
        return String(left.username).localeCompare(String(right.username), 'it', { sensitivity: 'base' });
      })
      .slice(0, 3)
      .map((player, index) => ({
        place: index + 1,
        country_alpha2: countries[0].country_alpha2,
        username: player.username,
        clerk_user_id: maskClerkUserIdForPublic(player.clerk_user_id),
        active_days_count: player.active_days_count,
        competition_stelline_total: player.competition_stelline_total,
      }))
    : null;

  return {
    success: true,
    weeks: selectedWeeks,
    countries,
    podium,
    team_details: publicTeamDetails,
    stacanovisti_podium: stacanovistiPodium,
  };
};
